import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { api, errorMessage } from "../api/client.ts";
import type { LookupData } from "../api/types.ts";
import { IconClose, IconSearch } from "../components/Icons.tsx";
import { Spinner, StatusPill } from "../components/ui.tsx";
import { formatTime, formatUnits, tokenOf } from "../lib/format.ts";
import { useDebounced, useMedia } from "../lib/hooks.ts";
import { useT } from "../i18n/index.tsx";
import { useSession } from "../lib/session.tsx";

type Hit = { kind: "deposit" | "payout"; id: string; title: string; sub: string; status: string; amount: string; when: string };

function hits(d: LookupData | null): Hit[] {
  if (!d) return [];
  return [
    ...d.deposits.map((x) => ({
      kind: "deposit" as const, id: x.id, title: `${formatUnits(x.amount, x.decimals)} ${tokenOf(x.asset)}`, sub: x.player_name ? `${x.player_name} · ${x.player_ref}` : x.player_ref, status: x.status,
      amount: x.amount, when: x.detected_at,
    })),
    ...d.payouts.map((x) => ({
      kind: "payout" as const, id: x.id, title: `${formatUnits(x.amount, x.decimals)} ${tokenOf(x.asset)}`, sub: x.player_ref ? (x.player_name ? `${x.player_name} · ${x.player_ref}` : x.player_ref) : x.request_id, status: x.status,
      amount: x.amount, when: x.created_at,
    })),
  ];
}

/**
 * Topbar quick lookup: player ref / request ID (from the start), address or tx hash (exact).
 * "/" focuses it; one match + Enter opens it; arrows move through several. Phones: a search icon that expands.
 */
export function QuickLookup() {
  const t = useT();
  const { canAny } = useSession();
  const phone = useMedia("(max-width: 960px)");
  const [expanded, setExpanded] = useState(false);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<LookupData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const listId = useId();
  const term = useDebounced(q.trim(), 300);
  const allowed = canAny("deposits:view", "payouts:view");

  // "/" anywhere (outside form fields) focuses the lookup.
  useEffect(() => {
    if (!allowed) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey || el.closest("input, textarea, select, [contenteditable=true]") || document.querySelector("dialog[open]")) return;
      e.preventDefault();
      setExpanded(true);
      requestAnimationFrame(() => input.current?.focus());
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [allowed]);

  useEffect(() => {
    if (term.length < 2) {
      setData(null);
      setError(null);
      return;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    api.get<LookupData>("/lookup", { q: term })
      .then((r) => {
        if (!alive) return;
        setData(r);
        setActive(0);
        setOpen(true);
      })
      .catch((e) => alive && setError(errorMessage(e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [term]);

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) {
        setOpen(false);
        if (phone && !q) setExpanded(false);
      }
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [phone, q]);

  if (!allowed) return null;
  const list = hits(data);
  const go = (h: Hit) => {
    setOpen(false);
    setQ("");
    setExpanded(false);
    input.current?.blur();
    navigate(`/${h.kind === "deposit" ? "deposits" : "payouts"}?open=${encodeURIComponent(h.id)}`);
  };
  const show = open && term.length >= 2 && (!!data || !!error);

  if (phone && !expanded) {
    return (
      <button type="button" className="icon-btn lookup-icon" aria-label={t.shell.lookupButton} onClick={() => { setExpanded(true); requestAnimationFrame(() => input.current?.focus()); }}>
        <IconSearch />
      </button>
    );
  }

  return (
    <div className={`lookup ${phone ? "lookup-phone" : ""}`} ref={box}>
      <IconSearch size={17} />
      <input
        ref={input}
        type="search"
        role="combobox"
        aria-expanded={show}
        aria-controls={listId}
        aria-activedescendant={show && list[active] ? `${listId}-${active}` : undefined}
        aria-label={t.shell.lookupLabel}
        placeholder={t.shell.lookupPlaceholder}
        value={q}
        spellCheck={false}
        autoComplete="off"
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            if (q) setQ("");
            else { setExpanded(false); input.current?.blur(); }
            setOpen(false);
          } else if (e.key === "ArrowDown" && list.length) {
            e.preventDefault();
            setActive((a) => (a + 1) % list.length);
          } else if (e.key === "ArrowUp" && list.length) {
            e.preventDefault();
            setActive((a) => (a - 1 + list.length) % list.length);
          } else if (e.key === "Enter") {
            e.preventDefault();
            const h = list.length === 1 ? list[0] : list[active];
            if (h && term === q.trim()) go(h);
          }
        }}
      />
      {loading ? <Spinner /> : !phone && !q && <kbd className="kbd" aria-hidden="true">/</kbd>}
      {phone && (
        <button type="button" className="icon-btn lookup-close" aria-label={t.shell.closeLookup} onClick={() => { setQ(""); setExpanded(false); setOpen(false); }}>
          <IconClose size={18} />
        </button>
      )}
      {show && (
        <div className="lookup-panel" id={listId} role="listbox" aria-label={t.shell.matches}>
          {error ? (
            <p className="lookup-empty">{error}</p>
          ) : !list.length ? (
            <p className="lookup-empty">{t.shell.noMatch}</p>
          ) : (
            (["deposit", "payout"] as const).map((kind) => {
              const rows = list.map((h, i) => ({ h, i })).filter((x) => x.h.kind === kind);
              if (!rows.length) return null;
              return (
                <div key={kind} role="group" aria-label={kind === "deposit" ? t.shell.nav.deposits : t.shell.nav.payouts}>
                  <div className="lookup-group">{kind === "deposit" ? t.shell.nav.deposits : t.shell.nav.payouts}</div>
                  {rows.map(({ h, i }) => (
                    <div key={h.id} id={`${listId}-${i}`} role="option" aria-selected={i === active} className={`lookup-item ${i === active ? "active" : ""}`}
                      onMouseEnter={() => setActive(i)} onMouseDown={(e) => { e.preventDefault(); go(h); }}>
                      <span className="lookup-main">
                        <strong>{h.title}</strong>
                        <span className="mono-sm muted">{h.sub}</span>
                      </span>
                      <span className="lookup-meta">
                        <StatusPill status={h.status} />
                        <span className="muted small">{formatTime(h.when)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
